import {rectSortingStrategy, SortableContext} from '@dnd-kit/sortable';
import React, {memo, useMemo} from 'react';

import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {LayedOutPanelWithRef} from '../../util/panelTypes';
import {WorkspacePanelContainer} from '../Panel/WorkspacePanel/WorkspacePanel2';
import {
  useWorkspaceFlowCurrentPage,
  useWorkspaceFlowLayout,
  useWorkspaceFlowLayoutHeight,
} from './useWorkspaceFlowLayout';
import {getDndSortablePanelId} from './utils';

const WorkspaceFlowLayoutComp = ({
  filteredPanels,
  panelBankSectionConfigRef,
  isProjectReadOnly,
}: {
  filteredPanels: LayedOutPanelWithRef[];
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  isProjectReadOnly: boolean;
}) => {
  const items = useMemo(() => {
    return filteredPanels.map(panel => getDndSortablePanelId(panel.ref));
  }, [filteredPanels]);

  const {
    columnsPerPage,
    gutterWidth,
    maxPage,
    panelHeight,
    panelsPerPage,
    panelsPerRow,
    panelWidth,
    rowsPerPage,
  } = useWorkspaceFlowLayout({
    panelBankSectionConfigRef,
    numTotalPanelsInSection: filteredPanels.length,
  });

  const {currentPage} = useWorkspaceFlowCurrentPage({
    panelBankSectionConfigRef,
    maxPage,
  });

  const flowLayoutHeight = useWorkspaceFlowLayoutHeight({
    gutterWidth,
    numTotalPanelsInSection: filteredPanels.length,
    panelHeight,
    panelsPerRow,
    rowsPerPage,
  });

  const filteredPanelsOnActivePage = useMemo(() => {
    const startIndex = currentPage * panelsPerPage;
    const endIndex = startIndex + panelsPerPage;

    // Do not loop through every panel to determine if it should be
    // rendered in the section. Just take advantage of array slicing.
    return filteredPanels.slice(startIndex, endIndex);
  }, [filteredPanels, currentPage, panelsPerPage]);

  return (
    <SortableContext items={items} strategy={rectSortingStrategy}>
      <div
        // TODO - spacing around panels are not correct since the panel height and widths are calculated based on flow config values
        // We don't need to rely on the gutterWdith for flowConfig, so we should move off it before releasing and give back real estate to the user.
        className="mx-20 grid"
        style={{
          gridTemplateColumns: `repeat(${columnsPerPage}, 1fr)`,
          height: flowLayoutHeight,
        }}>
        {filteredPanelsOnActivePage.map(panel => (
          <WorkspacePanelContainer
            isProjectReadOnly={isProjectReadOnly}
            key={getDndSortablePanelId(panel.ref)}
            panelBankSectionConfigRef={panelBankSectionConfigRef}
            panelHeight={panelHeight}
            panelRef={panel.ref}
            panelWidth={panelWidth}
          />
        ))}
      </div>
    </SortableContext>
  );
};

export const WorkspaceFlowLayout = memo(WorkspaceFlowLayoutComp);
